/**
 * Criteria list: collapsible current-Policy cards, ranked search hits.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { useNotification } from '../../hooks/useNotification';
import * as api from '../../services/api';
import { Criteria, CriteriaPurpose, Policy, Purpose } from '../../types';
import { HighlightedText } from '../common/highlightText';
import SearchableSelect from '../common/SearchableSelect';
import Loading from '../Loading';
import CriteriaPolicyList from './CriteriaPolicyList';
import type { ArchiveFilter, GranularityFilter } from './CriteriaPolicyList';
import PolicyHistoryDialog from './PolicyHistoryDialog';
import {
  currentPolicy,
  DEFAULT_SEARCH_SCOPE,
  matchesCriterionSearch,
  policiesForCriterion,
  policyVersionOptionLabel,
  SearchScope,
} from './criteriaPolicySearch';

const CriteriaManagement = () => {
  const [criteria, setCriteria] = useState<Criteria[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [allPurposes, setAllPurposes] = useState<Purpose[]>([]);
  const [criteriaPurposes, setCriteriaPurposes] = useState<CriteriaPurpose[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState<Criteria | null>(null);
  const [newCriteria, setNewCriteria] = useState<Partial<Criteria>>({
    name: '',
    granularity: 'sentence',
  });
  const [policyText, setPolicyText] = useState('');
  const [originalPolicyText, setOriginalPolicyText] = useState('');
  const [selectedPurposeIds, setSelectedPurposeIds] = useState<string[]>([]);
  const [isEditingPolicy, setIsEditingPolicy] = useState(false);
  const [viewedPolicyId, setViewedPolicyId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchScope, setSearchScope] = useState<SearchScope>(DEFAULT_SEARCH_SCOPE);
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>('active');
  const [granularityFilter, setGranularityFilter] = useState<GranularityFilter>('all');
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [historyCriterion, setHistoryCriterion] = useState<Criteria | null>(null);
  const { notification, showNotification, hideNotification } = useNotification();

  useEffect(() => {
    setIsLoadingData(true);
    const loadAllData = async () => {
      try {
        await Promise.all([
          fetchCriteriaData(),
          fetchPoliciesData(),
          fetchPurposesData(),
          fetchCriteriaPurposesData(),
        ]);
      } catch (error) {
        console.error('Error fetching initial data for Criteria Management:', error);
      } finally {
        setIsLoadingData(false);
      }
    };
    loadAllData();
  }, []);

  const fetchCriteriaData = async () => {
    try {
      const data = await api.fetchCriteria();
      setCriteria(data);
    } catch (error) {
      console.error('Error fetching criteria:', error);
      throw error;
    }
  };

  const fetchPoliciesData = async () => {
    try {
      const data = await api.fetchPolicies();
      setPolicies(data);
    } catch (error) {
      console.error('Error fetching policies:', error);
      throw error;
    }
  };

  const fetchPurposesData = async () => {
    try {
      const data = await api.fetchPurposes();
      setAllPurposes(data);
    } catch (error) {
      console.error('Error fetching purposes:', error);
      throw error;
    }
  };

  const fetchCriteriaPurposesData = async () => {
    try {
      const data = await api.fetchCriteriaPurposes();
      setCriteriaPurposes(data);
    } catch (error) {
      console.error('Error fetching criteria purposes:', error);
      throw error;
    }
  };

  const archiveFiltered = useMemo(
    () =>
      criteria.filter(criterion => {
        if (archiveFilter === 'active') return !criterion.isArchived;
        if (archiveFilter === 'archived') return Boolean(criterion.isArchived);
        return true;
      }),
    [criteria, archiveFilter]
  );

  const granularityFiltered = useMemo(
    () =>
      granularityFilter === 'all'
        ? archiveFiltered
        : archiveFiltered.filter(criterion => criterion.granularity === granularityFilter),
    [archiveFiltered, granularityFilter]
  );

  const filteredCriteria = useMemo(
    () =>
      granularityFiltered.filter(criterion => {
        const policy = currentPolicy(criterion, policies);
        return matchesCriterionSearch(criterion, policy?.text, searchQuery, searchScope);
      }),
    [granularityFiltered, policies, searchQuery, searchScope]
  );

  const editingPolicyVersions = useMemo(
    () => (editingCriteria ? policiesForCriterion(policies, editingCriteria.id) : []),
    [editingCriteria, policies]
  );

  const isViewingCurrentPolicy =
    !editingCriteria || viewedPolicyId === (editingCriteria.policyId ?? '');
  const viewedPolicy = editingPolicyVersions.find(policy => policy.id === viewedPolicyId);
  const displayedPolicyText = isViewingCurrentPolicy ? policyText : (viewedPolicy?.text ?? '');
  const searchNeedle = searchQuery.trim();

  const handleOpenDialog = (criterion?: Criteria) => {
    if (criterion) {
      setEditingCriteria(criterion);
      setNewCriteria({
        name: criterion.name,
        granularity: criterion.granularity,
        policyId: criterion.policyId,
      });
      const policy = policies.find(p => p.id === criterion.policyId);
      const currentPolicyText = policy ? policy.text : '';
      setPolicyText(currentPolicyText);
      setOriginalPolicyText(currentPolicyText);
      setViewedPolicyId(criterion.policyId ?? '');
      const currentCriterionPurposes = criteriaPurposes
        .filter(cp => cp.criteriaId === criterion.id)
        .map(cp => cp.purposeId);
      setSelectedPurposeIds(currentCriterionPurposes);
    } else {
      setEditingCriteria(null);
      setNewCriteria({
        name: '',
        granularity: 'sentence',
      });
      setPolicyText('');
      setOriginalPolicyText('');
      setViewedPolicyId('');
      setSelectedPurposeIds([]);
    }
    setIsEditingPolicy(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCriteria(null);
    setNewCriteria({
      name: '',
      granularity: 'sentence',
    });
    setPolicyText('');
    setOriginalPolicyText('');
    setSelectedPurposeIds([]);
    setIsEditingPolicy(false);
    setViewedPolicyId('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewCriteria(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<'sentence' | 'paragraph' | 'essay'>) => {
    const { name, value } = e.target;
    setNewCriteria(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePurposeSelectChange = (event: SelectChangeEvent<string | string[]>) => {
    const {
      target: { value },
    } = event;
    setSelectedPurposeIds(typeof value === 'string' ? value.split(',') : value);
  };

  const handlePolicyTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPolicyText(e.target.value);
  };

  const handleViewedPolicyChange = (event: SelectChangeEvent<string>) => {
    const nextId = event.target.value;
    setViewedPolicyId(nextId);
    if (editingCriteria && nextId !== editingCriteria.policyId) {
      setIsEditingPolicy(false);
    }
  };

  const handleCreateCriteria = async () => {
    try {
      let criterionIdToUpdate = editingCriteria ? editingCriteria.id : null;
      let initialPurposeIds: string[] = [];

      if (editingCriteria) {
        initialPurposeIds = criteriaPurposes
          .filter(cp => cp.criteriaId === editingCriteria.id)
          .map(cp => cp.purposeId);

        let policyIdToUpdate = editingCriteria.policyId; // Default to existing policyId

        if (policyText !== originalPolicyText) {
          // Policy text has changed, create a new policy
          const newPolicy = await api.createPolicy({
            text: policyText,
            criteriaId: editingCriteria.id,
          });
          policyIdToUpdate = newPolicy.id;
        }

        await api.updateCriteria(editingCriteria.id, {
          ...newCriteria, // Contains name, granularity from the form
          policyId: policyIdToUpdate, // Use new or existing policyId
        } as Criteria);
        // criterionIdToUpdate is already set from editingCriteria.id
      } else {
        // Criterion first, then its policy. The old order created the policy with an
        // empty criteriaId and back-filled it with a PUT, which mutated a policy row.
        // Policies are immutable (docs/adr/0001-policy-versions-and-eval-eligibility.md#immutability), and a
        // policy needs its criteriaId at insert to be assigned a version at all
        // (docs/adr/0001-policy-versions-and-eval-eligibility.md#versioning), so the order is reversed.
        const createdCriteria = await api.createCriteria({
          ...newCriteria,
          policyId: null,
        } as Criteria);
        criterionIdToUpdate = createdCriteria.id;

        const policy = await api.createPolicy({
          text: policyText,
          criteriaId: createdCriteria.id,
        });

        await api.updateCriteria(createdCriteria.id, {
          ...newCriteria,
          policyId: policy.id,
        } as Criteria);
        // initialPurposeIds remains empty as it's a new criterion
      }

      // Update CriteriaPurposes if criterionIdToUpdate is available
      if (criterionIdToUpdate) {
        const finalCriterionId = criterionIdToUpdate; // Ensures type is string for the operations below
        const currentSelectedPurposeIdsSet = new Set(selectedPurposeIds);
        const originalPurposeIdsSet = new Set(initialPurposeIds);

        const purposesToAddPromises: Promise<any>[] = [];
        currentSelectedPurposeIdsSet.forEach(purposeId => {
          if (!originalPurposeIdsSet.has(purposeId)) {
            purposesToAddPromises.push(
              api.createCriteriaPurpose({ criteriaId: finalCriterionId, purposeId })
            );
          }
        });

        const purposesToRemovePromises: Promise<any>[] = [];
        originalPurposeIdsSet.forEach(purposeId => {
          if (!currentSelectedPurposeIdsSet.has(purposeId)) {
            const cpToRemove = criteriaPurposes.find(
              cp => cp.criteriaId === finalCriterionId && cp.purposeId === purposeId
            );
            if (cpToRemove) {
              purposesToRemovePromises.push(api.deleteCriteriaPurpose(cpToRemove.id));
            }
          }
        });

        await Promise.all([...purposesToAddPromises, ...purposesToRemovePromises]);
      }

      fetchCriteriaData();
      fetchPoliciesData();
      fetchCriteriaPurposesData();
      handleCloseDialog();
      showNotification(
        `Criterion ${editingCriteria ? 'updated' : 'created'} successfully.`,
        'success'
      );
    } catch (error) {
      console.error('Error saving criterion:', error);
      showNotification('Error saving criterion. Please try again.', 'error');
    }
  };

  return (
    <>
      {isLoadingData ? (
        <Loading />
      ) : (
        <CriteriaPolicyList
          criteria={filteredCriteria}
          allCriteriaCount={granularityFiltered.length}
          policies={policies}
          allPurposes={allPurposes}
          criteriaPurposes={criteriaPurposes}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          searchScope={searchScope}
          onSearchScopeChange={setSearchScope}
          archiveFilter={archiveFilter}
          onArchiveFilterChange={setArchiveFilter}
          granularityFilter={granularityFilter}
          onGranularityFilterChange={setGranularityFilter}
          onEdit={criterion => handleOpenDialog(criterion)}
          onBrowseHistory={criterion => setHistoryCriterion(criterion)}
          onAdd={() => handleOpenDialog()}
        />
      )}

      <PolicyHistoryDialog
        open={historyCriterion !== null}
        criterion={historyCriterion}
        policies={policies}
        onClose={() => setHistoryCriterion(null)}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        closeAfterTransition={false}
      >
        <DialogTitle>{editingCriteria ? 'Edit Criterion' : 'Add New Criterion'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            name="name"
            label="Name"
            fullWidth
            value={newCriteria.name}
            onChange={handleInputChange}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Granularity</InputLabel>
            <Select
              name="granularity"
              value={newCriteria.granularity}
              label="Granularity"
              onChange={handleSelectChange}
            >
              <MenuItem value="sentence">Sentence</MenuItem>
              <MenuItem value="paragraph">Paragraph</MenuItem>
              <MenuItem value="essay">Essay</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="purpose-select-label">Purposes</InputLabel>
            <SearchableSelect
              labelId="purpose-select-label"
              id="purpose-select"
              multiple
              value={selectedPurposeIds}
              onChange={handlePurposeSelectChange}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map(value => {
                    const purpose = allPurposes.find(p => p.id === value);
                    return <Chip key={value} label={purpose ? purpose.name : value} />;
                  })}
                </Box>
              )}
              label="Purposes"
              options={allPurposes.map(purpose => ({
                value: purpose.id,
                label: purpose.name,
              }))}
            />
          </FormControl>
          {editingCriteria && editingPolicyVersions.length > 0 ? (
            <FormControl fullWidth margin="normal">
              <InputLabel id="policy-version-select-label">Policy Version</InputLabel>
              <Select
                labelId="policy-version-select-label"
                id="policy-version-select"
                value={viewedPolicyId}
                label="Policy Version"
                onChange={handleViewedPolicyChange}
              >
                {editingPolicyVersions.map(policy => (
                  <MenuItem key={policy.id} value={policy.id}>
                    {policyVersionOptionLabel(policy, policy.id === editingCriteria.policyId)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="subtitle1">Policy:</Typography>
              {isViewingCurrentPolicy ? (
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setIsEditingPolicy(!isEditingPolicy)}
                >
                  {isEditingPolicy ? 'Preview' : 'Edit'}
                </Button>
              ) : (
                <Chip size="small" label="Read-only" variant="outlined" />
              )}
            </Box>
            {!isViewingCurrentPolicy ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Older Policy Versions are read-only. Editing and saving still apply only to the
                current Policy.
              </Typography>
            ) : null}
            {isEditingPolicy && isViewingCurrentPolicy ? (
              <TextField
                fullWidth
                multiline
                rows={8}
                value={policyText}
                onChange={handlePolicyTextChange}
                margin="normal"
                variant="outlined"
              />
            ) : searchNeedle ? (
              <Box sx={{ p: 2, bgcolor: 'grey.50', minHeight: '200px', whiteSpace: 'pre-wrap' }}>
                <HighlightedText text={displayedPolicyText} query={searchNeedle} />
              </Box>
            ) : (
              <Box sx={{ p: 2, bgcolor: 'grey.50', minHeight: '200px' }}>
                <ReactMarkdown remarkPlugins={[remarkBreaks]}>{displayedPolicyText}</ReactMarkdown>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleCreateCriteria} color="primary">
            {editingCriteria ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CriteriaManagement;
